import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Printer, Music, Paperclip, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { format, parseISO } from 'date-fns';

// Screen: hide the portal div (print-only content).
// Print: hide ALL body children except the portal, reset body height so no phantom pages.
const PRINT_CSS = `
#program-print-portal { display: none; }

@media print {
  @page { margin: 1.5cm; size: A4 portrait; }
  html, body {
    height: auto !important;
    overflow: visible !important;
    background: white !important;
  }
  body > *:not(#program-print-portal) { display: none !important; }
  #program-print-portal {
    display: block !important;
    position: static !important;
    width: 100% !important;
  }
  /* Force all text and icons to full black regardless of app theme */
  #program-print-portal,
  #program-print-portal * {
    color: #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  #program-print-portal svg {
    color: #000 !important;
    stroke: currentColor;
  }
}
`;

const formatDate = (date, time) => {
  try {
    const d = parseISO(date);
    const dateStr = format(d, 'EEEE, MMMM d, yyyy');
    return time ? `${dateStr} · ${time}` : dateStr;
  } catch {
    return date || '';
  }
};

const PrintContent = ({ formData, sections, getSongMeta }) => {
  const multiSection = sections.length > 1;
  const svcType = formData.service_type || '';
  const svcName = formData.name || '';
  const heading = svcName && svcName !== svcType ? `${svcType} — ${svcName}` : svcType;

  return (
    <div className="font-sans p-8 max-w-3xl mx-auto text-slate-900">
      {/* Service info */}
      <div className="mb-6 pb-4 border-b border-slate-300">
        <h1 className="text-2xl font-bold text-slate-900">{heading}</h1>
        {(formData.date || formData.time) && (
          <p className="text-sm text-slate-600 mt-1">{formatDate(formData.date, formData.time)}</p>
        )}
        {formData.location && (
          <p className="text-sm text-slate-500 mt-0.5">{formData.location}</p>
        )}
        {formData.notes && (
          <p className="text-xs text-slate-500 mt-2 whitespace-pre-wrap">{formData.notes}</p>
        )}
      </div>

      {/* Program */}
      {sections.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-6">No program items.</p>
      ) : (
        <div className="space-y-6">
          {sections.map((section) => {
            const sectionTitle = section.header?.title || 'Section';
            const showFieldset = multiSection || sectionTitle !== 'Default';
            const legendLabel = sectionTitle === 'Default' ? 'General' : sectionTitle;
            const sectionItems = section.items || [];
            return (
              <div key={section.id}>
                {showFieldset && (
                  <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2 pb-1 border-b border-slate-200">
                    {legendLabel}
                  </h2>
                )}
                {sectionItems.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No items</p>
                ) : (
                  <ol className="space-y-3 list-none">
                    {sectionItems.map((item, idx) => {
                      const songs = item.songs || [];
                      const attachments = item.attachments || [];
                      const links = item.links || [];
                      return (
                        <li key={item.id} className="flex gap-3">
                          <span className="text-sm font-semibold text-green-700 w-6 flex-shrink-0 text-right">
                            {idx + 1}.
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900">{item.name || item.title}</p>
                            {(songs.length > 0 || attachments.length > 0 || links.length > 0) && (
                              <ul className="mt-1.5 space-y-0.5">
                                {songs.map((s) => {
                                  const meta = getSongMeta(s.song_id);
                                  const title = s.title || meta.title || 'Unknown';
                                  const key = s.key || meta.key;
                                  const tempo = s.tempo || meta.tempo;
                                  return (
                                    <li key={s.song_id} className="flex items-start gap-2 text-xs text-slate-600">
                                      <Music className="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5" />
                                      <span className="flex-1 min-w-0 break-words">
                                        {title}
                                        {(key || tempo) && (
                                          <span className="text-slate-400 ml-1">
                                            {[key, tempo && `${tempo} BPM`].filter(Boolean).map((t, i) => (
                                              <span key={i}>{i > 0 ? ' · ' : '· '}{t}</span>
                                            ))}
                                          </span>
                                        )}
                                      </span>
                                    </li>
                                  );
                                })}
                                {attachments.map((att) => (
                                  <li key={att.attachment_id || att.filename} className="flex items-center gap-2 text-xs text-slate-600">
                                    <Paperclip className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                    <span>{att.label || att.filename}</span>
                                  </li>
                                ))}
                                {links.map((link) => (
                                  <li key={link.id} className="flex items-center gap-2 text-xs text-slate-600">
                                    <ExternalLink className="w-3 h-3 text-blue-400 flex-shrink-0" />
                                    <span className="truncate">{link.label || link.title || link.url}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            {item.notes && (
                              <p className="text-xs text-slate-500 mt-1.5 whitespace-pre-wrap">{item.notes}</p>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <p className="mt-10 pt-4 border-t border-slate-200 text-[10px] text-slate-400 text-right">
        Printed from Church Service Management · {new Date().toLocaleString()}
      </p>
    </div>
  );
};

const ProgramPrintModal = ({ formData, sections, getSongMeta, onClose }) => {
  // Inject print CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'program-print-css';
    style.textContent = PRINT_CSS;
    document.head.appendChild(style);
    return () => document.getElementById('program-print-css')?.remove();
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <>
      {/* ── Screen preview modal ───────────────────────────────────────── */}
      <div
        className="fixed inset-0 z-50 flex flex-col bg-slate-100"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        data-testid="print-modal-container"
      >
        {/* Toolbar */}
        <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200 shadow-sm">
          <button onClick={onClose} data-testid="print-modal-close"
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
            <X className="w-4 h-4" />
          </button>
          <span className="text-sm font-semibold text-slate-700 flex-1">Print Preview</span>
          <Button size="sm" className="rounded-full gap-2 bg-green-700 hover:bg-green-800"
            data-testid="print-btn" onClick={() => window.print()}>
            <Printer className="w-4 h-4" />
            Print
          </Button>
        </div>

        {/* Scrollable preview */}
        <div className="flex-1 overflow-y-auto py-8 px-4">
          <div className="bg-white mx-auto max-w-2xl shadow rounded-xl">
            <PrintContent formData={formData} sections={sections} getSongMeta={getSongMeta} />
          </div>
        </div>
      </div>

      {/* ── Print-only portal (hidden on screen, shown on print) ──────── */}
      {ReactDOM.createPortal(
        <div id="program-print-portal">
          <PrintContent formData={formData} sections={sections} getSongMeta={getSongMeta} />
        </div>,
        document.body
      )}
    </>
  );
};

export default ProgramPrintModal;
